/**
 * Accessible Combobox (input + listbox)
 *
 * - Supports async data via getItems(query)
 * - Debounced updates + stale request protection
 * - Tracks "selected" value (must be chosen from list)
 *
 * Options:
 * - input: HTMLInputElement (required)
 * - listbox: HTMLElement|null (optional; if null, component creates one)
 * - minQueryLen: number (default 1)
 * - debounceMs: number (default 100)
 * - ariaLabel: string (for created listbox)
 * - getItems: async (query) => string[]  (required)
 * - onInput: () => void
 * - onSelect: (value) => void
 * - onBlurInvalid: () => void
 * - onStatus: (text) => void
 * - onError: (text) => void
 */
export default class Combobox {
  /**
   * Rank string items by relevance for a query.
   *
   * Priority:
   * 1) Exact match
   * 2) Prefix match
   * 3) Word-boundary match
   * 4) Generic substring match
   * Then shorter strings first, then original order.
   *
   * @param {string[]} items
   * @param {string} query
   * @param {number} limit
   * @return {string[]}
   */
  static rankMatches(items, query, limit = Infinity) {
    const q = String(query || '').trim().toLowerCase();

    if (!q || !Array.isArray(items) || !items.length) {
      return [];
    }

    const matches = [];

    items.forEach((item, index) => {
      const name = String(item || '');
      const candidate = name.toLowerCase();
      const hitPos = candidate.indexOf(q);

      if (hitPos === -1) {
        return;
      }

      let score = 3;
      if (candidate === q) {
        score = 0;
      } else if (candidate.startsWith(q)) {
        score = 1;
      } else if (hitPos > 0 && candidate[hitPos - 1] === ' ') {
        score = 2;
      }

      matches.push({
        score,
        index,
        name,
        length: candidate.length
      });
    });

    matches.sort((a, b) => {
      if (a.score !== b.score) {
        return a.score - b.score;
      }
      if (a.length !== b.length) {
        return a.length - b.length;
      }
      return a.index - b.index;
    });

    return matches.slice(0, limit).map((m) => m.name);
  }

  constructor(opts) {
    this.input = opts.input;
    this.listbox = opts.listbox || null;

    this.minQueryLen = typeof opts.minQueryLen === 'number' ? opts.minQueryLen : 1;
    this.debounceMs = typeof opts.debounceMs === 'number' ? opts.debounceMs : 100;
    this.ariaLabel = opts.ariaLabel || 'Options';

    this.getItems = opts.getItems;

    this.onInputCb = opts.onInput || null;
    this.onSelectCb = opts.onSelect || null;
    this.onBlurInvalidCb = opts.onBlurInvalid || null;
    this.onStatus = opts.onStatus || null;
    this.onError = opts.onError || null;

    this.timer = null;
    this.requestId = 0;

    this.items = [];
    this.activeIndex = -1;
    this.selectedValue = '';

    this.optionEls = [];
    this.hoverIndex = -1;
    this.boundDocMouseDown = (e) => this.onDocumentMouseDown(e);

    this.ensureMarkup();
    this.bind();
  }

  ensureMarkup() {
    // Create listbox if missing (HubSpot)
    if (!this.listbox) {
      this.listbox = document.createElement('div');
      this.listbox.className = 'pb-combobox-listbox';
      this.listbox.setAttribute('role', 'listbox');
      this.listbox.setAttribute('aria-label', this.ariaLabel);
      this.listbox.hidden = true;

      // ensure an id for aria-controls
      const id = `pb-listbox-${Math.random().toString(16).slice(2)}`;
      this.listbox.id = id;

      // Insert after input
      this.input.insertAdjacentElement('afterend', this.listbox);
    }

    // ARIA on input
    if (this.listbox.id) {
      this.input.setAttribute('aria-controls', this.listbox.id);
      // aria-owns is optional; keep if you already use it in Twig, but not required here
    }

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('autocomplete', 'off');
  }

  bind() {
    this.onInput = () => {
      // User typed -> selection no longer guaranteed
      if (String(this.input.value || '') !== this.selectedValue) {
        this.selectedValue = '';
      }

      if (this.onInputCb) {
        this.onInputCb();
      }

      this.queueUpdate(String(this.input.value || ''));
    };

    this.onFocus = () => {
      const v = String(this.input.value || '').trim();
      if (!v) {
        return;
      }

      // If not selected, show suggestions
      if (!this.isSelected()) {
        this.queueUpdate(v);
      }
    };

    this.onKeydown = (e) => this.handleKeydown(e);

    this.onBlur = () => {
      // let click selection win before validating
      window.setTimeout(() => {
        this.close();
        this.setActiveIndex(-1);

        const v = String(this.input.value || '').trim();

        // empty is fine (up to form validation)
        if (!v) {
          return;
        }

        // If not selected, treat as invalid
        if (!this.isSelected()) {
          if (this.onBlurInvalidCb) {
            this.onBlurInvalidCb();
          }
        }
      }, 0);
    };

    this.onOptionMouseDown = (e) => {
      // prevent blur before click
      e.preventDefault();
    };

    this.onOptionClick = (e) => {
      const el = e.target.closest('[role="option"]');
      if (!el) {
        return;
      }
      const value = String(el.getAttribute('data-value') || '');
      if (!value) {
        return;
      }
      this.select(value);
    };

    // Mouse/pointer parity: hovering an option highlights it (does not select)
    this.onOptionPointerMove = (e) => {
      const el = e.target.closest('[role="option"]');
      if (!el) {
        return;
      }

      const idx = parseInt(el.getAttribute('data-index') || '', 10);
      if (!Number.isFinite(idx)) {
        return;
      }

      // Avoid redundant DOM work for high-frequency pointer events
      if (idx === this.hoverIndex) {
        return;
      }

      this.hoverIndex = idx;
      this.setActiveIndex(idx);
    };

    this.onListboxPointerLeave = () => {
      this.hoverIndex = -1;
      this.setActiveIndex(-1);
    };

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocus);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('blur', this.onBlur);

    this.listbox.addEventListener('mousedown', this.onOptionMouseDown);
    this.listbox.addEventListener('click', this.onOptionClick);

    // Use pointer events for consistent hover behavior across input devices
    this.listbox.addEventListener('pointermove', this.onOptionPointerMove);
    this.listbox.addEventListener('pointerleave', this.onListboxPointerLeave);

    document.addEventListener('mousedown', this.boundDocMouseDown);
  }

  destroy() {
    this.clearTimer();
    document.removeEventListener('mousedown', this.boundDocMouseDown);

    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('focus', this.onFocus);
    this.input.removeEventListener('keydown', this.onKeydown);
    this.input.removeEventListener('blur', this.onBlur);

    this.listbox.removeEventListener('mousedown', this.onOptionMouseDown);
    this.listbox.removeEventListener('click', this.onOptionClick);
    this.listbox.removeEventListener('pointermove', this.onOptionPointerMove);
    this.listbox.removeEventListener('pointerleave', this.onListboxPointerLeave);
  }

  clearTimer() {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  onDocumentMouseDown(e) {
    const t = e.target;
    if (!t) {
      return;
    }
    if (t === this.input || this.listbox.contains(t)) {
      return;
    }
    this.close();
    this.setActiveIndex(-1);
  }

  queueUpdate(query) {
    const q = String(query || '');
    this.clearTimer();

    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.update(q);
    }, this.debounceMs);
  }

  async update(query) {
    const q = String(query || '').trim();

    if (q.length < this.minQueryLen) {
      this.items = [];
      this.render([]);
      this.close();
      return;
    }

    // request guard
    this.requestId += 1;
    const myId = this.requestId;

    try {
      const items = await this.getItems(q);

      // stale
      if (myId !== this.requestId) {
        return;
      }

      const safeItems = Array.isArray(items) ? items.filter(Boolean).map(String) : [];

      this.items = safeItems;
      this.render(safeItems);

      if (safeItems.length) {
        this.open();
      } else {
        this.close();
      }

      this.setActiveIndex(-1);
    } catch (err) {
      if (myId !== this.requestId) {
        return;
      }

      this.items = [];
      this.render([]);
      this.close();

      if (this.onError) {
        this.onError(err);
      }

      // eslint-disable-next-line no-console
      console.error('[combobox] getItems failed:', err);
    }
  }

  render(items) {
    // minimal DOM churn: rebuild innerHTML (small lists)
    const html = items
      .slice(0, 25)
      .map((name, idx) => {
        const id = `${this.listbox.id}-opt-${idx}`;
        const safe = this.escapeHtml(name);
        return `<div role="option" id="${id}" class="pb-combobox-option" data-index="${idx}" data-value="${safe}" aria-selected="false">${safe}</div>`;
      })
      .join('');

    this.listbox.innerHTML = html;
    this.optionEls = Array.from(this.listbox.querySelectorAll('[role="option"]'));
  }

  open() {
    this.listbox.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.hoverIndex = -1;
  }

  handleKeydown(e) {
    const key = e.key;

    if (key === 'ArrowDown') {
      if (this.listbox.hidden) {
        this.update(String(this.input.value || ''));
      }
      if (this.items.length) {
        e.preventDefault();
        this.moveActive(1);
      }
      return;
    }

    if (key === 'ArrowUp') {
      if (!this.listbox.hidden && this.items.length) {
        e.preventDefault();
        this.moveActive(-1);
      }
      return;
    }

    if (key === 'Home') {
      if (!this.listbox.hidden && this.items.length) {
        e.preventDefault();
        this.setActiveIndex(0);
        this.scrollActiveIntoView();
      }
      return;
    }

    if (key === 'End') {
      if (!this.listbox.hidden && this.items.length) {
        e.preventDefault();
        this.setActiveIndex(this.items.length - 1);
        this.scrollActiveIntoView();
      }
      return;
    }

    if (key === 'Enter') {
      if (!this.listbox.hidden && this.activeIndex >= 0 && this.activeIndex < this.items.length) {
        e.preventDefault();
        this.select(this.items[this.activeIndex]);
      }
      return;
    }

    if (key === 'Escape') {
      if (!this.listbox.hidden) {
        e.preventDefault();
        this.close();
        this.setActiveIndex(-1);
      }
    }
  }

  moveActive(delta) {
    const len = this.items.length;
    if (!len) {
      return;
    }
    const next = Math.min(len - 1, Math.max(0, this.activeIndex + delta));
    this.setActiveIndex(next);
    this.scrollActiveIntoView();
  }

  setActiveIndex(idx) {
    this.activeIndex = idx;
    this.hoverIndex = idx;

    this.optionEls.forEach((el) => {
      el.setAttribute('aria-selected', 'false');
      el.classList.remove('is-active');
    });

    if (idx < 0) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }

    const el = this.optionEls[idx];
    if (!el) {
      return;
    }

    el.setAttribute('aria-selected', 'true');
    el.classList.add('is-active');
    this.input.setAttribute('aria-activedescendant', el.id);
  }

  scrollActiveIntoView() {
    const el = this.optionEls[this.activeIndex];
    if (!el) {
      return;
    }

    const list = this.listbox;
    const listTop = list.scrollTop;
    const listBottom = listTop + list.clientHeight;

    const optTop = el.offsetTop;
    const optBottom = optTop + el.offsetHeight;

    if (optTop < listTop) {
      list.scrollTop = optTop;
    } else if (optBottom > listBottom) {
      list.scrollTop = optBottom - list.clientHeight;
    }
  }

  select(value) {
    this.input.value = value;
    this.selectedValue = value;

    this.close();
    this.setActiveIndex(-1);

    if (this.onSelectCb) {
      this.onSelectCb(value);
    }
  }

  isSelected() {
    return String(this.input.value || '') === String(this.selectedValue || '');
  }

  setSelectedValue(value) {
    this.selectedValue = String(value || '');
  }

  escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
